import { TuettuKieli } from '@lemon/core/model/common'
import { CountryLocalizationService } from '../translation.service'
import { LaskuSharedService } from './lasku-shared.service'

import {
  Lasku,
  LaskuBase,
  Laskuasetukset,
  LaskuasetuksetSpostille,
  LaskunSahkopostipohja,
  LaskunumeroTyyppi,
  SAHKOPOSTIPOHJAN_MUUTTUJAT,
  SAHKOPOSTIPOHJAN_VANHAT_MUUTTUJAT,
  SahkopostipohjanMuuttuja,
} from '../../model/lasku'

export class LaskuSpostiService {
  static readonly OLETUSTEMPLATE = 8372593
  static readonly HYVITYS_OLETUSTEMPLATE = 47743775

  private templates = {}

  annaSahkopostipohjanPostmarkNumero(pohja: LaskunSahkopostipohja, kieli: TuettuKieli): number {
    const template = this.templates[pohja]
    if (template) {
      const id = template[kieli]
      if (id) {
        return id
      }
    }
    return LaskuSpostiService.OLETUSTEMPLATE
  }

  constructor(
    private shared: LaskuSharedService,
    private maaService: CountryLocalizationService,
  ) {
    this.templates[LaskunSahkopostipohja.PERINTEINEN] = {}
    this.templates[LaskunSahkopostipohja.PERINTEINEN]['fi'] = 3935461
    this.templates[LaskunSahkopostipohja.PERINTEINEN]['en'] = 4686262
    this.templates[LaskunSahkopostipohja.PERINTEINEN]['sv'] = 0

    this.templates[LaskunSahkopostipohja.MUISTUTUS_PERINTEINEN] = {}
    this.templates[LaskunSahkopostipohja.MUISTUTUS_PERINTEINEN]['fi'] = 4593684
    this.templates[LaskunSahkopostipohja.MUISTUTUS_PERINTEINEN]['en'] = 4686261
    this.templates[LaskunSahkopostipohja.MUISTUTUS_PERINTEINEN]['sv'] = 0

    this.templates[LaskunSahkopostipohja.MUISTUTUS_LEMONTREE] = {}
    this.templates[LaskunSahkopostipohja.MUISTUTUS_LEMONTREE]['fi'] = 44240346
    this.templates[LaskunSahkopostipohja.MUISTUTUS_LEMONTREE]['en'] = 44239074
    this.templates[LaskunSahkopostipohja.MUISTUTUS_LEMONTREE]['sv'] = 0

    this.templates[LaskunSahkopostipohja.JATKUVALASKUTUS_PERINTEINEN] = {}
    this.templates[LaskunSahkopostipohja.JATKUVALASKUTUS_PERINTEINEN]['fi'] = LaskuSpostiService.OLETUSTEMPLATE
    this.templates[LaskunSahkopostipohja.JATKUVALASKUTUS_PERINTEINEN]['en'] = LaskuSpostiService.OLETUSTEMPLATE
    this.templates[LaskunSahkopostipohja.JATKUVALASKUTUS_PERINTEINEN]['sv'] = LaskuSpostiService.OLETUSTEMPLATE

    this.templates[LaskunSahkopostipohja.HYVITYS_PERINTEINEN] = {}
    this.templates[LaskunSahkopostipohja.HYVITYS_PERINTEINEN]['fi'] = 47742724
    this.templates[LaskunSahkopostipohja.HYVITYS_PERINTEINEN]['en'] = 47742723
    this.templates[LaskunSahkopostipohja.HYVITYS_PERINTEINEN]['sv'] = LaskuSpostiService.HYVITYS_OLETUSTEMPLATE
  }

  private _annaSummaJosPohjassaOnSummamuuttuja(korvattava: string, kasiteltava: LaskuBase): string {
    const summamuuttujat = Object.values(SAHKOPOSTIPOHJAN_MUUTTUJAT.summa)
    const pohjassaOnSummamuuttuja = summamuuttujat.some(summamuuttuja => korvattava.includes(summamuuttuja))
    if (!pohjassaOnSummamuuttuja) {
      return ''
    }
    const maksettavaa = this.shared.annaMaksettavaa(kasiteltava)
    return this.shared.formatoiBigRaha(maksettavaa, kasiteltava)
  }

  korvaaMuuttujat(korvattava: string, asetukset: Laskuasetukset, juurilasku: Lasku, kasiteltava: LaskuBase): string {
    if (korvattava) {
      const muotoiltuYtunnus = this.shared.muotoileYtunnusLaskunTyypinPerusteella(kasiteltava, asetukset.ytunnus)

      korvattava = korvattava.replace(/{{yrityksen_nimi}}/g, asetukset.nimi)
      korvattava = korvattava.replace(/{{ytunnus}}/g, muotoiltuYtunnus)
      korvattava = korvattava.replace(/{{katuosoite}}/g, asetukset.katuosoite)
      korvattava = korvattava.replace(/{{postinumero}}/g, asetukset.postinro)
      korvattava = korvattava.replace(/{{postitoimipaikka}}/g, asetukset.postitmp)
      korvattava = korvattava.replace(/{{maa}}/g, this.maaService.getName(asetukset.maakoodi, kasiteltava.kieli))

      const viimeisinMuokattu = this.shared.annaViimeisinMuokattuLasku(juurilasku)
      const laskunumero = this.shared.annaMuotoiltuLaskunumero(juurilasku, kasiteltava)
      const erapaiva = this.shared.formatoiPvm(viimeisinMuokattu.erapvml, viimeisinMuokattu.erapvm, kasiteltava)
      korvattava = korvattava.replace(/{{laskunumero}}/g, laskunumero)
      korvattava = korvattava.replace(/{{erapaiva}}/g, erapaiva)

      const muuttujienArvot: Record<SahkopostipohjanMuuttuja, string> = {
        yrityksenNimi: asetukset.nimi,
        laskunNumero: laskunumero,
        erapaiva: erapaiva,
        asiakkaanNimi: kasiteltava.asiakas?.nimi ?? '',
        summa: this._annaSummaJosPohjassaOnSummamuuttuja(korvattava, kasiteltava),
      }
      for (const muuttuja of Object.keys(SAHKOPOSTIPOHJAN_MUUTTUJAT) as SahkopostipohjanMuuttuja[]) {
        for (const merkinta of Object.values(SAHKOPOSTIPOHJAN_MUUTTUJAT[muuttuja])) {
          korvattava = korvattava.split(merkinta).join(muuttujienArvot[muuttuja])
        }
      }

      return korvattava
    }

    return ''
  }

  muunnaVanhatMuuttujatLuettaviksi(teksti: string, kieli: TuettuKieli): string {
    if (!teksti) {
      return teksti
    }
    let muunnettu = teksti
    for (const muuttuja of Object.keys(SAHKOPOSTIPOHJAN_VANHAT_MUUTTUJAT) as SahkopostipohjanMuuttuja[]) {
      muunnettu = muunnettu.split(SAHKOPOSTIPOHJAN_VANHAT_MUUTTUJAT[muuttuja]).join(SAHKOPOSTIPOHJAN_MUUTTUJAT[muuttuja][kieli])
    }
    return muunnettu
  }

  annaSpostiAsetukset(kasiteltava: LaskuBase, onkoMuistutus: boolean, laskuasetukset: Laskuasetukset): LaskuasetuksetSpostille {
    const onkoHyvitys = kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS

    if (onkoHyvitys && laskuasetukset && laskuasetukset.hyvitysSpostiasetukset && laskuasetukset.hyvitysSpostiasetukset[kasiteltava.kieli]) {
      return laskuasetukset.hyvitysSpostiasetukset[kasiteltava.kieli]
    } else if (!onkoHyvitys && onkoMuistutus && laskuasetukset && laskuasetukset.muistutusSpostiasetukset && laskuasetukset.muistutusSpostiasetukset[kasiteltava.kieli]) {
      return laskuasetukset.muistutusSpostiasetukset[kasiteltava.kieli]
    } else if (!onkoHyvitys && laskuasetukset && laskuasetukset.spostiasetukset && laskuasetukset.spostiasetukset[kasiteltava.kieli]) {
      return laskuasetukset.spostiasetukset[kasiteltava.kieli]
    }

    if (onkoHyvitys) {
      return {
        aihe: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.hyvityssposti.aihe', kasiteltava),
        kieli: kasiteltava.kieli,
        teksti: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.hyvityssposti.teksti', kasiteltava),
        otsikko: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.hyvityssposti.otsikko', kasiteltava),
        template: LaskunSahkopostipohja.HYVITYS_PERINTEINEN,
      }
    } else if (onkoMuistutus) {
      return {
        aihe: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.muistutussposti.aihe', kasiteltava),
        kieli: kasiteltava.kieli,
        teksti: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.muistutussposti.teksti', kasiteltava),
        otsikko: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.muistutussposti.otsikko', kasiteltava),
        template: LaskunSahkopostipohja.MUISTUTUS_PERINTEINEN,
      }
    } else {
      return {
        aihe: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.sposti.aihe', kasiteltava),
        kieli: kasiteltava.kieli,
        teksti: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.sposti.teksti', kasiteltava),
        otsikko: this.shared.annaLokalisoituMerkkijono('lasku.spostipohjat.oletukset.sposti.otsikko', kasiteltava),
        template: LaskunSahkopostipohja.PERINTEINEN,
      }
    }
  }
}
